import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "./UiDb";
import { BusinessConfigData } from "../../interfaces/BusinessObject";
import {
  fetchBusinessConfig,
  updateBusinessConfig,
  disableBusinessConfig,
} from "../../services/thunks/BusinessConfigThunks";
import { resetAllState } from "./resetAllState";

interface BusinessConfigState {
  /**
   * One business's config, cached by businessId once fetched — every
   * consumer (business page, cart, the config editor) shares this one entry
   * instead of each holding its own copy. `undefined` means "never
   * fetched"; `{}` means the fetch succeeded and the business has no fields
   * configured yet — those are NOT the same, check `successMap` to tell
   * them apart (same convention as BusinessSectionsDb.treesByBusinessId).
   */
  configByBusinessId: Record<string, BusinessConfigData>;
  /** loadingMap / errorMap / successMap for fetch/update/disable, all keyed
   *  by businessId — same three-map shape every other slice uses, keyed by
   *  businessId since every tracked action here is inherently per-business. */
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

const initialState: BusinessConfigState = {
  configByBusinessId: {},
  loadingMap: {},
  errorMap: {},
  successMap: {},
};

const businessConfigSlice = createSlice({
  name: "businessConfig",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessConfig.pending, (state, action) => {
        state.loadingMap[action.meta.arg] = "Loading business config...";
        state.errorMap[action.meta.arg] = null;
      })
      .addCase(fetchBusinessConfig.fulfilled, (state, action) => {
        const { businessId, config } = action.payload;
        state.loadingMap[businessId] = null;
        state.successMap[businessId] = true;
        state.configByBusinessId[businessId] = config;
      })
      .addCase(fetchBusinessConfig.rejected, (state, action) => {
        state.loadingMap[action.meta.arg] = null;
        state.errorMap[action.meta.arg] = action.payload as string;
      });

    builder
      .addCase(updateBusinessConfig.pending, (state, action) => {
        state.loadingMap[action.meta.arg.businessId] = "Saving business config...";
        state.errorMap[action.meta.arg.businessId] = null;
      })
      .addCase(updateBusinessConfig.fulfilled, (state, action) => {
        const { businessId, config } = action.payload;
        state.loadingMap[businessId] = null;
        state.successMap[businessId] = true;
        state.configByBusinessId[businessId] = config;
      })
      .addCase(updateBusinessConfig.rejected, (state, action) => {
        state.loadingMap[action.meta.arg.businessId] = null;
        state.errorMap[action.meta.arg.businessId] = action.payload as string;
      });

    builder
      .addCase(disableBusinessConfig.pending, (state, action) => {
        state.loadingMap[action.meta.arg] = "Disabling business config...";
        state.errorMap[action.meta.arg] = null;
      })
      .addCase(disableBusinessConfig.fulfilled, (state, action) => {
        const { businessId, config } = action.payload;
        state.loadingMap[businessId] = null;
        state.successMap[businessId] = true;
        state.configByBusinessId[businessId] = config;
      })
      .addCase(disableBusinessConfig.rejected, (state, action) => {
        state.loadingMap[action.meta.arg] = null;
        state.errorMap[action.meta.arg] = action.payload as string;
      });

    builder.addCase(resetAllState, () => initialState);
  },
});

export default businessConfigSlice.reducer;

// ─── Selectors ───────────────────────────────────────────────────────────────

/** A business's cached config, or `undefined` if never fetched. Pair with
 *  selectBusinessConfigHasLoaded to tell "still loading"/"never fetched"
 *  apart from "loaded and empty" (see BusinessConfigState's doc comment). */
export const selectBusinessConfig = (
  state: RootState,
  businessId: string
): BusinessConfigData | undefined => state.businessConfig.configByBusinessId[businessId];

export const selectBusinessConfigIsLoading = (state: RootState, businessId: string): boolean =>
  Boolean(state.businessConfig.loadingMap[businessId]);

export const selectBusinessConfigHasLoaded = (state: RootState, businessId: string): boolean =>
  Boolean(state.businessConfig.successMap[businessId]);

export const selectBusinessConfigError = (state: RootState, businessId: string): string | null =>
  state.businessConfig.errorMap[businessId] ?? null;
